// src/pages/CalendarPage.js
import React, { useState, useEffect, useMemo } from "react";
import DetailsCalendarModal from "../components/DetailsCalendarModal";
import "./CalendarPage.css";
import { API_URL } from "../config"; // ✅ jedno źródło API (z /api na końcu)

/* ---------- STAŁE ----------- */
const monthNames = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień",
];
const weekDaysShort = ["Pn","Wt","Śr","Cz","Pt","Sb","Nd"];
const pad = (n) => n.toString().padStart(2, "0");

/* ================ KOMPONENT ================ */
export default function CalendarPage() {
  const today = new Date();

  /* -------- stan widoku kalendarza -------- */
  const [month, setMonth] = useState(today.getMonth());
  const [year,  setYear]  = useState(today.getFullYear());

  /* -------- urodziny -------- */
  const [showBirthdays, setShowBirthdays] = useState(false);
  const [birthdays,     setBirthdays]     = useState([]);

  /* -------- rocznice -------- */
  const [showAnniv, setShowAnniv] = useState(false);
  const [anniv,     setAnniv]     = useState([]);

  /* -------- czas pracy -------- */
  const [showWorktime,   setShowWorktime]   = useState(false);
  const [users,          setUsers]          = useState([]);
  const [selectedUser,   setSelectedUser]   = useState("");
  const [worktimeMonth,  setWorktimeMonth]  = useState({});   // {MM-DD: hrs}

  /* -------- modal -------- */
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDate,    setModalDate]    = useState(null);     // Date obj
  const [modalData,    setModalData]    = useState({
    birthdays:[], anniversaries:[], workEntries:[]
  });

  /* ---------- FETCH-y ---------- */

  // birthday list
  useEffect(() => {
    if (!showBirthdays || birthdays.length) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/calendar/birthdays`, { credentials:"include" });
        if (!r.ok) throw new Error();
        const data = await r.json();
        setBirthdays(Array.isArray(data) ? data : []);
      } catch { console.error("Błąd pobierania /birthdays"); }
    })();
  }, [showBirthdays, birthdays.length]);

  // anniversary list
  useEffect(() => {
    if (!showAnniv || anniv.length) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/calendar/anniversaries`, { credentials:"include" });
        if (!r.ok) throw new Error();
        const data = await r.json();
        setAnniv(Array.isArray(data) ? data : []);
      } catch { console.error("Błąd pobierania /anniversaries"); }
    })();
  }, [showAnniv, anniv.length]);

  // user list
  useEffect(() => {
    if (!showWorktime || users.length) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/calendar/users`, { credentials:"include" });
        if (!r.ok) throw new Error();
        const data = await r.json();
        setUsers(Array.isArray(data) ? data : []);
      } catch { console.error("Błąd pobierania /users"); }
    })();
  }, [showWorktime, users.length]);

  // worktime summary per day
  useEffect(() => {
    if (!showWorktime || !selectedUser) { setWorktimeMonth({}); return; }

    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/calendar/worktime/${selectedUser}?year=${year}&month=${month+1}`,
          { credentials:"include" }
        );
        if (!r.ok) throw new Error();
        const rows = await r.json();     // [{ work_date, total_hours }]
        const map  = {};
        (Array.isArray(rows) ? rows : []).forEach(({ work_date, total_hours }) => {
          const d = new Date(work_date);
          const key = `${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
          map[key] = Number(total_hours);
        });
        setWorktimeMonth(map);
      } catch { console.error("Błąd pobierania /worktime"); }
    })();
  }, [showWorktime, selectedUser, year, month]);

  /* ---------- MAP WYDARZEŃ ---------- */
  const eventsMap = useMemo(() => {
    const map = {};

    // birthdays
    if (showBirthdays) {
      birthdays.forEach((u) => {
        const d = new Date(u.birth_date);
        const key = `${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
        const txt = `Urodziny: ${u.first_name} ${u.last_name} – ${year-d.getFullYear()} lat(a)`;
        (map[key] = map[key] || []).push({ txt, type:"bday" });
      });
    }

    // anniversaries + company b-day
    if (showAnniv) {
      anniv.forEach((u) => {
        const d = new Date(u.hire_date);
        const key = `${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
        const txt = `Rocznica: ${u.first_name} ${u.last_name} – ${year-d.getFullYear()} lat(a) w firmie`;
        (map[key] = map[key] || []).push({ txt, type:"anniv" });
      });
      const firmYears = year - 2001;
      (map["04-18"] = map["04-18"] || []).push({
        txt:`${firmYears} Rocznica Założenia Firmy`, type:"anniv"
      });
    }

    // hours
    if (showWorktime && selectedUser) {
      Object.entries(worktimeMonth).forEach(([key, hrs]) => {
        (map[key] = map[key] || []).push({ txt:`${hrs} h`, type:"hours" });
      });
    }
    return map;
  }, [
    birthdays, showBirthdays,
    anniv, showAnniv,
    worktimeMonth, showWorktime, selectedUser,
    year
  ]);

  /* ---------- HELPERS ---------- */
  const firstDay    = new Date(year, month, 1);
  const startIdx    = (firstDay.getDay()+6)%7;
  const daysInMonth = new Date(year, month+1, 0).getDate();

  const cells = [];
  for (let i=0;i<startIdx;i++) cells.push(null);
  for (let d=1;d<=daysInMonth;d++) cells.push(d);
  while (cells.length%7!==0) cells.push(null);

  const goToday   = () => { setMonth(today.getMonth()); setYear(today.getFullYear()); };
  const prevMonth = () => month===0 ? (setMonth(11), setYear(y=>y-1)) : setMonth(m=>m-1);
  const nextMonth = () => month===11? (setMonth(0), setYear(y=>y+1))  : setMonth(m=>m+1);

  /* ---------- CLICK na komórkę ---------- */
  const handleDayClick = async (day) => {
    if (!day) return;
    const keyMMDD  = `${pad(month+1)}-${pad(day)}`;
    // unikamy konwersji do UTC – składamy datę „ręcznie”
    const isoDate  = `${year}-${pad(month + 1)}-${pad(day)}`;
    const jsDate   = new Date(year, month, day);

    /* ---- zbieramy dane lokalnie ---- */
    const bdays = showBirthdays
      ? birthdays
          .filter(({ birth_date }) => {
            const d = new Date(birth_date);
            return d.getDate() === day && d.getMonth() === month;
          })
          .map((u) => ({
            name:`${u.first_name} ${u.last_name}`,
            age: year - new Date(u.birth_date).getFullYear()
          }))
      : [];

    const annivs = [];
    if (showAnniv) {
      anniv.forEach((u) => {
        const d = new Date(u.hire_date);
        if (d.getDate() === day && d.getMonth() === month) {
          annivs.push(
            `Rocznica: ${u.first_name} ${u.last_name} – ${year-d.getFullYear()} lat(a) w firmie`
          );
        }
      });
      if (keyMMDD === "04-18") {
        annivs.push(`${year-2001} Rocznica Założenia Firmy`);
      }
    }

    /* ---- worktime (z backendu) ---- */
    let workEntries = [];
    if (showWorktime && selectedUser) {
      try {
        const r = await fetch(
          `${API_URL}/calendar/worktime/${selectedUser}/details?date=${isoDate}`,
          { credentials:"include" }
        );
        if (r.ok) {
          const data = await r.json();
          workEntries = Array.isArray(data) ? data : [];
        }
      } catch { console.error("Błąd worktime details"); }
    }

    setModalDate(jsDate);
    setModalData({ birthdays:bdays, anniversaries:annivs, workEntries });
    setModalVisible(true);
  };

  /* -------------- RENDER -------------- */
  return (
    <>
      {/* ----------- MODAL ----------- */}
      <DetailsCalendarModal
        visible={modalVisible}
        date={modalDate}
        birthdays={modalData.birthdays}
        anniversaries={modalData.anniversaries}
        workEntries={modalData.workEntries}
        onClose={() => setModalVisible(false)}
      />

      <div className="calendar-container">
        {/* ===== lewa kolumna ===== */}
        <div className="calendar-main">
          <div className="calendar-header">
            <button className="nav-btn" onClick={prevMonth}>❮</button>
            <h2 className="month-title">{monthNames[month]} {year}</h2>
            <button className="nav-btn" onClick={nextMonth}>❯</button>
            <button className="today-btn" onClick={goToday}>Dzisiaj</button>
          </div>

          {/* selecty */}
          <div className="select-row">
            <select className="modern-select" value={month}
                    onChange={(e)=>setMonth(Number(e.target.value))}>
              {monthNames.map((n,i)=> <option key={i} value={i}>{n}</option>)}
            </select>
            <select className="modern-select" value={year}
                    onChange={(e)=>setYear(Number(e.target.value))}>
              {Array.from({length:11},(_,i)=>2020+i).map(y=>
                <option key={y} value={y}>{y}</option>
              )}
            </select>
          </div>

          {/* siatka */}
          <div className="calendar-grid">
            {weekDaysShort.map(d=> <div key={d} className="day-header">{d}</div>)}

            {cells.map((day, idx) => {
              const key = day ? `${pad(month+1)}-${pad(day)}` : "";
              const evs = eventsMap[key] || [];
              return (
                <div
                  key={idx}
                  className={`day-cell ${day?"":"empty"}`}
                  onClick={()=>handleDayClick(day)}
                  style={{ cursor: day ? "pointer" : "default" }}
                >
                  {day && <span className="day-number">{day}</span>}
                  {evs.map((e,i)=>(
                    <div key={i} className={`event ${e.type}`}>{e.txt}</div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== prawa kolumna ===== */}
        <div className="calendar-settings">
          <h3>Ustawienia Kalendarza</h3>

          <label className="setting-row">
            <input type="checkbox"
                   checked={showBirthdays}
                   onChange={()=>setShowBirthdays(p=>!p)} />
            <span>Urodziny użytkowników</span>
          </label>

          <label className="setting-row">
            <input type="checkbox"
                   checked={showAnniv}
                   onChange={()=>setShowAnniv(p=>!p)} />
            <span>Rocznice firmowe</span>
          </label>

          <label className="setting-row">
            <input type="checkbox"
                   checked={showWorktime}
                   onChange={()=>{
                     setShowWorktime(p=>!p);
                     setSelectedUser("");
                     setWorktimeMonth({});
                   }} />
            <span>Czas pracy pracownika</span>
          </label>

          {showWorktime && users.length > 0 && (
            <select
              className="modern-select"
              style={{ marginTop:"0.5rem", width:"100%" }}
              value={selectedUser}
              onChange={(e)=>setSelectedUser(e.target.value)}
            >
              <option value="">– wybierz pracownika –</option>
              {users.map(u=>(
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </>
  );
}
